import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Store, ShieldCheck, Clock, Ticket, CheckCircle2 } from "lucide-react";

function RedeemCouponCard() {
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [last, setLast] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/partners/coupons/redeem", { code_or_token: code.trim() });
      setLast(res.data);
      toast.success(`Coupon ${res.data.code} redeemed`);
      setCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to redeem");
    } finally { setBusy(false); }
  };

  const label = last ? (
    last.discount_type === "free" ? "FREE PUC" :
    last.discount_type === "percent" ? `${last.discount_value}% off` :
    `₹${last.discount_value} off`
  ) : "";

  return (
    <div className="border border-primary/20 bg-primary/5 rounded-2xl p-5 mb-8" data-testid="redeem-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
        <Ticket className="w-4 h-4" /> Redeem gift voucher
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <Input
          data-testid="redeem-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter 8-char code (e.g. HM34GNND) or paste QR link"
          className="rounded-lg bg-white border-border h-11 font-mono flex-1"
        />
        <Button data-testid="redeem-submit-btn" type="submit" disabled={busy} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11 sm:w-56">
          {busy ? "Checking…" : "Redeem coupon"}
        </Button>
      </form>
      {last && (
        <div data-testid="redeem-result" className="mt-3 flex items-center gap-3 text-sm bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <div>
            <div className="font-semibold text-emerald-800">Redeemed · <span className="font-mono">{last.code}</span></div>
            <div className="text-xs text-emerald-700">{label} · {last.service_scope === "any" ? "Any vehicle" : last.service_scope.replace("_", " ")}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const statusColor = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-primary/10 text-primary border-primary/30",
  rejected: "bg-red-100 text-red-800 border-red-200",
  confirmed: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const VEHICLE_OPTIONS = [
  { id: "two_wheeler", label: "Two-Wheeler" },
  { id: "three_wheeler", label: "Three-Wheeler" },
  { id: "four_wheeler", label: "Four-Wheeler" },
  { id: "commercial", label: "Commercial" },
];

export default function PartnerDashboard() {
  const [partner, setPartner] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    store_name: "", address: "", city: "", pincode: "", state: "",
    contact_name: "", contact_phone: "", contact_email: "",
    licence_number: "", licence_authority: "", licence_expiry: "",
    latitude: "", longitude: "",
  });
  const [servicesOffered, setServicesOffered] = useState(["two_wheeler", "four_wheeler"]);

  // Complete-dialog state
  const [completeFor, setCompleteFor] = useState(null); // booking id
  const [validityDate, setValidityDate] = useState("");

  const load = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get("/partners/mine"),
        api.get("/partners/bookings/incoming"),
      ]);
      setPartner(p.data);
      setBookings(b.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleService = (id) => {
    setServicesOffered((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (servicesOffered.length === 0) { toast.error("Select at least one vehicle category"); return; }
    setSaving(true);
    try {
      const payload = { ...form, services_offered: servicesOffered };
      // Coerce optional lat/lng to numbers if provided
      payload.latitude = form.latitude === "" ? null : parseFloat(form.latitude);
      payload.longitude = form.longitude === "" ? null : parseFloat(form.longitude);
      if (Number.isNaN(payload.latitude)) payload.latitude = null;
      if (Number.isNaN(payload.longitude)) payload.longitude = null;
      await api.post("/partners", payload);
      toast.success("Store submitted for admin approval");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateBooking = async (bid, status) => {
    if (status === "completed") {
      // ask for validity date via dialog
      setCompleteFor(bid);
      const defaultDate = new Date();
      defaultDate.setMonth(defaultDate.getMonth() + 6);
      setValidityDate(defaultDate.toISOString().slice(0, 10));
      return;
    }
    try {
      await api.put(`/bookings/${bid}/status`, { status });
      toast.success(`Booking marked ${status}`);
      const b = await api.get("/partners/bookings/incoming");
      setBookings(b.data);
    } catch {
      toast.error("Failed to update");
    }
  };

  const confirmComplete = async () => {
    if (!validityDate) { toast.error("Enter validity date"); return; }
    try {
      await api.put(`/bookings/${completeFor}/status`, { status: "completed", cert_valid_until: validityDate });
      toast.success("Booking completed. Customer will be reminded 3 days before expiry.");
      setCompleteFor(null);
      const b = await api.get("/partners/bookings/incoming");
      setBookings(b.data);
    } catch {
      toast.error("Failed");
    }
  };

  if (loading) return <div className="min-h-screen bg-background text-foreground"><Navbar /><div className="p-8 text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Partner dashboard</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-10">Your PUC Centre</h1>

        {!partner ? (
          <Card className="rounded-lg border-border bg-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <Store className="w-6 h-6 text-primary" />
              <h2 className="font-display text-2xl font-bold">Register your centre</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-8 max-w-2xl">Fill in your store & RTO licence details. An admin will verify and activate your listing within 24 hours.</p>

            <form onSubmit={submit} className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store name</Label>
                <Input data-testid="partner-store-name" required value={form.store_name} onChange={setField("store_name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Textarea data-testid="partner-address" required value={form.address} onChange={setField("address")} className="rounded-lg bg-secondary border-border mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">City</Label>
                <Input data-testid="partner-city" required value={form.city} onChange={setField("city")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">State</Label>
                <Input data-testid="partner-state" required value={form.state} onChange={setField("state")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Pincode</Label>
                <Input data-testid="partner-pincode" required value={form.pincode} onChange={setField("pincode")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact person</Label>
                <Input data-testid="partner-contact-name" required value={form.contact_name} onChange={setField("contact_name")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact phone</Label>
                <Input data-testid="partner-contact-phone" required value={form.contact_phone} onChange={setField("contact_phone")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Contact email</Label>
                <Input data-testid="partner-contact-email" type="email" required value={form.contact_email} onChange={setField("contact_email")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div className="md:col-span-2 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-4"><ShieldCheck className="w-4 h-4" /> Government licence</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Licence number</Label>
                <Input data-testid="partner-licence-number" required value={form.licence_number} onChange={setField("licence_number")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Issuing authority</Label>
                <Input data-testid="partner-licence-authority" required value={form.licence_authority} onChange={setField("licence_authority")} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="e.g. Delhi RTO" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Licence expiry</Label>
                <Input data-testid="partner-licence-expiry" type="date" required value={form.licence_expiry} onChange={setField("licence_expiry")} className="rounded-lg bg-secondary border-border h-11 mt-2" />
              </div>
              <div className="md:col-span-2 pt-4 border-t border-border">
                <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Map coordinates (optional)</div>
                <p className="text-xs text-muted-foreground mb-3">Right-click your shop on <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Maps</a> and click the coordinates to copy them. Without coordinates, we&apos;ll use your address to place the map pin.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Latitude</Label>
                <Input data-testid="partner-latitude" type="text" inputMode="decimal" value={form.latitude} onChange={setField("latitude")} className="rounded-lg bg-secondary border-border h-11 mt-2 font-mono" placeholder="12.9716" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Longitude</Label>
                <Input data-testid="partner-longitude" type="text" inputMode="decimal" value={form.longitude} onChange={setField("longitude")} className="rounded-lg bg-secondary border-border h-11 mt-2 font-mono" placeholder="77.5946" />
              </div>
              <div className="md:col-span-2 pt-4 border-t border-border">
                <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">Vehicle categories you service</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="services-picker">
                  {VEHICLE_OPTIONS.map((o) => (
                    <label key={o.id} className="flex items-center gap-3 border border-border bg-secondary p-3 cursor-pointer hover:bg-white/5 transition-colors duration-200">
                      <Checkbox
                        data-testid={`service-${o.id}`}
                        checked={servicesOffered.includes(o.id)}
                        onCheckedChange={() => toggleService(o.id)}
                        className="rounded-lg data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <span className="text-sm">{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Button data-testid="partner-submit-btn" type="submit" disabled={saving} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 px-8">
                  {saving ? "Submitting…" : "Submit for approval"}
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-px bg-border mb-12" data-testid="partner-info">
              <div className="bg-card p-6 md:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Store</div>
                  <Badge className={`rounded-lg border ${statusColor[partner.status]}`}>{partner.status.toUpperCase()}</Badge>
                </div>
                <div className="font-display text-2xl font-bold tracking-tight">{partner.store_name}</div>
                <div className="text-sm text-muted-foreground mt-2">{partner.address}, {partner.city} {partner.pincode}</div>
              </div>
              <div className="bg-card p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Licence</div>
                <div className="font-mono text-lg">{partner.licence_number}</div>
                <div className="text-xs text-muted-foreground mt-2">{partner.licence_authority} · exp {partner.licence_expiry}</div>
                {partner.services_offered?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Categories</div>
                    <div className="flex flex-wrap gap-1">
                      {partner.services_offered.map((s) => (
                        <span key={s} className="text-xs bg-secondary px-2 py-1 border border-border">{s.replace("_", " ")}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {partner.status !== "approved" && (
              <div className="border border-amber-200 bg-amber-50 p-4 mb-8 flex items-center gap-3 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-900">Your centre is {partner.status}. It will appear to customers once approved by admin.</span>
              </div>
            )}

            <RedeemCouponCard />

            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Incoming bookings</h2>
            <div className="border border-border bg-card">
              <Table data-testid="partner-bookings-table">
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Customer</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Vehicle</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Slot</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No bookings yet</TableCell></TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.booking_id} className="border-border" data-testid={`partner-booking-${b.booking_id}`}>
                      <TableCell className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="font-medium">{b.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono">{b.vehicle_number}</div>
                        <div className="text-xs text-muted-foreground">{b.vehicle_type.replace("_", " ")} · {b.fuel_type}</div>
                        {b.coupon?.code && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 font-mono" data-testid={`booking-coupon-${b.booking_id}`}>
                            <Ticket className="w-3 h-3" /> {b.coupon.code} · {b.coupon.discount_type === "free" ? "FULL" : b.coupon.discount_type === "percent" ? `${b.coupon.discount_value}%` : `₹${b.coupon.discount_value}`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{b.slot_date}</div>
                        <div className="text-xs text-muted-foreground">{b.slot_time}</div>
                        {b.cert_valid_until && <div className="text-xs text-primary mt-1">PUC · {b.cert_valid_until}</div>}
                      </TableCell>
                      <TableCell><Badge className={`rounded-lg border ${statusColor[b.status]}`}>{b.status}</Badge></TableCell>
                      <TableCell>
                        <Select onValueChange={(v) => updateBooking(b.booking_id, v)}>
                          <SelectTrigger data-testid={`booking-status-${b.booking_id}`} className="rounded-lg bg-secondary border-border h-9 w-36 text-xs"><SelectValue placeholder="Update" /></SelectTrigger>
                          <SelectContent className="rounded-lg">
                            <SelectItem value="confirmed">Confirm</SelectItem>
                            <SelectItem value="completed">Complete + PUC date</SelectItem>
                            <SelectItem value="cancelled">Cancel</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Dialog open={!!completeFor} onOpenChange={(open) => !open && setCompleteFor(null)}>
              <DialogContent data-testid="complete-dialog" className="rounded-lg border-border bg-card">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight">Enter PUC certificate validity</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">The customer will be reminded automatically 3 days before this date.</p>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Valid until</Label>
                  <Input data-testid="cert-validity-date" type="date" value={validityDate} onChange={(e) => setValidityDate(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                </div>
                <DialogFooter>
                  <Button data-testid="complete-cancel" variant="outline" onClick={() => setCompleteFor(null)} className="rounded-lg border-border text-foreground hover:bg-secondary">Cancel</Button>
                  <Button data-testid="complete-confirm" onClick={confirmComplete} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Mark completed</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
